import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Mic, MicOff, Send, Square, Volume2, VolumeX, X, Sparkles, Loader2, RotateCcw } from 'lucide-react';
import { useVoiceIO } from './useVoiceIO';
import { AssistantResultCard } from './AssistantResultCard';

const API = process.env.REACT_APP_BACKEND_URL;
const GREETING = {
  assistant: 'Hi, I am AIRA. Tell me where you want to go: flights, hotels, cabs, insurance or tours. You can type or tap the mic.',
  planner: 'Tell me your destination, dates, budget, travellers and interests, and I will plan a day-by-day trip using real flight, hotel and tour results.',
};

const Bubble = ({ m }) => (
  <div className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
    <div data-testid={`assistant-msg-${m.role}`}
      className={`max-w-[88%] whitespace-pre-wrap rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${m.role === 'user' ? 'bg-[#E6006E] text-white rounded-br-md' : m.error ? 'bg-red-500/15 text-red-200 border border-red-500/30' : 'bg-white/[0.07] text-white/90 rounded-bl-md'}`}>
      {m.text}
    </div>
  </div>
);

const Status = ({ voice, loading }) => {
  const label = voice.listening ? 'Listening… tap stop when done' : voice.transcribing ? 'Transcribing…' : loading ? 'Searching real inventory…' : voice.speaking ? 'Speaking…' : null;
  if (!label) return null;
  return (
    <div className="flex items-center gap-2 px-4 py-1.5 text-xs text-[#FF8CC6]" data-testid="assistant-status">
      <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#E6006E] opacity-75" /><span className="relative inline-flex h-2 w-2 rounded-full bg-[#E6006E]" /></span>
      {label}
    </div>
  );
};

export const AssistantChat = ({ mode = 'assistant', className = '' }) => {
  const key = `aira_assistant_session_${mode}`;
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(key));
  const [messages, setMessages] = useState([{ role: 'assistant', text: GREETING[mode] }]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);
  const voiceRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, loading]);

  useEffect(() => {
    if (!sessionId) return;
    axios.get(`${API}/api/travel-assistant/session/${sessionId}`).then(({ data }) => {
      if (data.history?.length) setMessages([{ role: 'assistant', text: GREETING[mode] }, ...data.history.slice(-12)]);
    }).catch(() => { localStorage.removeItem(key); setSessionId(null); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const send = useCallback(async (text, fromVoice = false) => {
    const msg = text.trim();
    if (!msg || loading) return;
    voiceRef.current?.stopSpeaking();
    setMessages((p) => [...p, { role: 'user', text: msg }]);
    setInput('');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/travel-assistant/chat`, { message: msg, session_id: sessionId, mode }, { timeout: 240000 });
      setSessionId(data.session_id);
      localStorage.setItem(key, data.session_id);
      setMessages((p) => [...p, { role: 'assistant', text: data.reply, cards: data.cards }]);
      if (fromVoice) voiceRef.current?.speak(data.reply);
    } catch (e) {
      const reason = e.code === 'ECONNABORTED' ? 'The request timed out.' : 'I could not reach the assistant service.';
      setMessages((p) => [...p, { role: 'assistant', error: true, text: `${reason} Nothing was booked or charged. Please try again.` }]);
    } finally {
      setLoading(false);
    }
  }, [loading, sessionId, mode, key]);

  const onVoiceError = useCallback((t) => setMessages((p) => [...p, { role: 'assistant', error: true, text: t }]), []);
  const voice = useVoiceIO({ onTranscript: (t) => send(t, true), onError: onVoiceError });
  voiceRef.current = voice;

  const reset = () => {
    localStorage.removeItem(key);
    setSessionId(null);
    setMessages([{ role: 'assistant', text: GREETING[mode] }]);
  };

  return (
    <div className={`flex flex-col bg-[#14101F] text-white ${className}`} data-testid={`assistant-chat-${mode}`}>
      <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4" data-testid="assistant-messages">
        {messages.map((m, i) => (
          <div key={i} className="space-y-2">
            <Bubble m={m} />
            {m.cards?.length > 0 && <div className="space-y-2">{m.cards.map((c) => <AssistantResultCard key={c.id} card={c} />)}</div>}
          </div>
        ))}
        {loading && <div className="flex items-center gap-2 text-xs text-white/50"><Loader2 className="w-3.5 h-3.5 animate-spin" />AIRA is thinking…</div>}
        <div ref={endRef} />
      </div>
      <Status voice={voice} loading={loading} />
      <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="flex items-center gap-2 border-t border-white/10 p-3">
        <button type="button" onClick={reset} title="New conversation" data-testid="assistant-reset-btn" className="rounded-full p-2 text-white/50 hover:text-white hover:bg-white/10 transition-colors"><RotateCcw className="w-4 h-4" /></button>
        <button type="button" onClick={() => voice.setMuted(!voice.muted)} title={voice.muted ? 'Unmute voice replies' : 'Mute voice replies'} data-testid="assistant-mute-btn" className="rounded-full p-2 text-white/60 hover:text-white hover:bg-white/10 transition-colors">
          {voice.muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
        <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Type or speak your travel plan…" data-testid="assistant-input"
          className="flex-1 min-w-0 rounded-full bg-white/[0.07] px-4 py-2.5 text-sm text-white placeholder:text-white/40 outline-none focus:ring-2 focus:ring-[#E6006E]/60" />
        {voice.speaking && (
          <button type="button" onClick={voice.stopSpeaking} title="Stop speaking" data-testid="assistant-stop-speaking-btn" className="rounded-full bg-white/10 p-2.5 hover:bg-white/20 transition-colors"><Square className="w-4 h-4" /></button>
        )}
        <button type="button" onClick={voice.listening ? voice.stopListening : voice.startListening} disabled={voice.transcribing || loading}
          title={voice.listening ? 'Stop listening' : 'Speak'} data-testid="assistant-mic-btn"
          className={`rounded-full p-2.5 transition-colors disabled:opacity-40 ${voice.listening ? 'bg-red-500 animate-pulse' : 'bg-white/10 hover:bg-white/20'}`}>
          {voice.listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
        </button>
        <button type="submit" disabled={!input.trim() || loading} data-testid="assistant-send-btn" className="rounded-full bg-[#E6006E] p-2.5 transition-transform hover:scale-105 disabled:opacity-40"><Send className="w-4 h-4" /></button>
      </form>
    </div>
  );
};

export const TravelAssistantLauncher = ({ mode = 'assistant' }) => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => { setOpen(false); }, [pathname]);
  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 24, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24 }}
            className="fixed inset-x-2 bottom-2 top-16 z-[60] flex flex-col overflow-hidden rounded-3xl border border-white/10 shadow-2xl sm:inset-auto sm:bottom-24 sm:right-6 sm:h-[640px] sm:w-[420px]"
            data-testid="assistant-panel">
            <div className="flex items-center justify-between bg-[#1C1530] px-4 py-3 text-white">
              <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#FF4FA3]" /><span className="font-semibold">AIRA Travel Assistant</span></div>
              <button onClick={() => setOpen(false)} data-testid="assistant-close-btn" className="rounded-full p-1.5 hover:bg-white/10"><X className="w-4 h-4" /></button>
            </div>
            <AssistantChat mode={mode} className="flex-1 min-h-0" />
          </motion.div>
        )}
      </AnimatePresence>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="assistant-launcher-btn"
          className="fixed bottom-6 right-6 z-[60] flex items-center gap-2 rounded-full bg-[#E6006E] px-5 py-3.5 text-sm font-semibold text-white shadow-[0_10px_40px_rgba(230,0,110,0.45)] transition-transform hover:scale-105">
          <Mic className="w-4 h-4" /> Ask AIRA
        </button>
      )}
    </>
  );
};
